import type { ReactNode } from 'react';
import { UserPlus } from 'lucide-react';
import { css } from 'styled-system/css';

export function MockFrame({
  url,
  children,
  rounded,
  share,
  light,
  dots,
}: {
  url?: string;
  children: ReactNode;
  rounded?: boolean;
  share?: boolean;
  light?: boolean;
  dots?: boolean;
}) {
  return (
    <div
      data-rounded={rounded || undefined}
      data-light={light || undefined}
      className={css({
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        background: 'bg.sunken',
        border: '1px solid token(colors.border.hairline)',
        overflow: 'hidden',
        '--colors-border-hairline': '{colors.ink.700}',
        '&[data-rounded]': {
          borderRadius: '12px',
        },
        '&[data-light]': {
          colorScheme: 'light',
          '--colors-bg-page': '{colors.ink.25}',
          '--colors-bg-surface': '{colors.ink.0}',
          '--colors-bg-sunken': '{colors.ink.50}',
          '--colors-bg-subtle': '{colors.ink.100}',
          '--colors-bg-hover': '{colors.ink.100}',
          '--colors-bg-active': '{colors.ink.150}',
          '--colors-bg-inverse': '{colors.ink.950}',
          '--colors-text-primary': '{colors.ink.950}',
          '--colors-text-secondary': '{colors.ink.600}',
          '--colors-text-muted': '{colors.ink.500}',
          '--colors-text-faint': '{colors.ink.400}',
          '--colors-text-inverse': '{colors.ink.0}',
          '--colors-text-on-accent': '{colors.ink.0}',
          '--colors-border-hairline': '{colors.ink.200}',
          '--colors-border-default': '{colors.ink.300}',
          '--colors-border-strong': '{colors.ink.400}',
          '--colors-border-focus': '{colors.ink.950}',
          '--colors-accent-default': '{colors.ink.950}',
          '--colors-accent-hover': '{colors.ink.800}',
          '--colors-signal-error': '#c6433f',
          '--colors-signal-error-bg': '#fbeceb',
          '--colors-signal-valid': '#2f7d57',
          '--colors-signal-valid-bg': '#e9f4ee',
          '--colors-signal-merged': '{colors.prism.violet}',
          '--colors-signal-merged-bg': '#f3edfe',
        },
      })}
    >
      <div
        className={css({
          display: 'flex',
          alignItems: 'center',
          gap: '3',
          height: '36px',
          paddingInline: '3',
          borderBottom: '1px solid token(colors.border.hairline)',
          background: 'bg.surface',
          flexShrink: 0,
        })}
      >
        {dots !== false && (
          <span
            className={css({
              display: 'flex',
              gap: '1.5',
              '& > span': {
                width: '7px',
                height: '7px',
                borderRadius: 'full',
                background: 'border.default',
              },
            })}
          >
            <span />
            <span />
            <span />
          </span>
        )}
        {url && (
          <span
            className={css({
              flex: 1,
              fontFamily: 'mono',
              fontSize: '2xs',
              color: 'text.faint',
              letterSpacing: 'wide',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            })}
          >
            {url}
          </span>
        )}
        {!url && <span className={css({ flex: 1 })} />}
        {share && (
          <span
            data-light={light || undefined}
            className={css({
              display: 'inline-flex',
              alignItems: 'center',
              gap: '1.5',
              height: '22px',
              paddingInline: '2.5',
              fontSize: '2xs',
              fontWeight: 'semibold',
              color: 'bg.page',
              background:
                'color-mix(in srgb, token(colors.type.number) 48%, token(colors.ink.0))',
              borderRadius: 'full',
              flexShrink: 0,
              '& > svg': { width: '12px', height: '12px' },
              '&[data-light]': {
                color: '#0b57d0',
                background: '#c2e7ff',
              },
            })}
          >
            <UserPlus />
            Share
          </span>
        )}
        {share && (
          <span
            className={css({
              width: '22px',
              height: '22px',
              borderRadius: 'full',
              flexShrink: 0,
              background: 'bg.subtle',
            })}
          />
        )}
      </div>
      <div
        className={css({
          position: 'relative',
          flex: 1,
          minHeight: 0,
        })}
      >
        {children}
      </div>
    </div>
  );
}
